import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { imageUrl } from '../lib/images';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [behandlingerOpen, setBehandlingerOpen] = useState(false);
  const [mobileBehandlingerOpen, setMobileBehandlingerOpen] = useState(false);
  const [mobileKontaktOpen, setMobileKontaktOpen] = useState(false);
  const [kontaktOpen, setKontaktOpen] = useState(false);
  const location = useLocation();

  const logoUrl = 'https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/djurs-massage-logo.jpg';
  const bookingUrl = 'https://djursmassage.app4.geckobooking.dk/site/booking.php?show=new_booking&icCode=69a506a6f06a0ca7022b880e8f6bcb65b11332&bId=11332&aDIfH=1&dTpl=1&sSToken=267bea05a53684a4a7d7ad2e6bae564a';
  const giftcardUrl = 'https://djursmassage.app4.geckobooking.dk/site/index.php?icCode=e6868204e7e11708974faa3b47f24d3db11332&dTpl=1';

  // Close menus on page navigation
  useEffect(() => {
    setMobileMenuOpen(false);
    setBehandlingerOpen(false);
    setKontaktOpen(false);
  }, [location.pathname]);

  const isBehandlingerActive = ['/behandlinger', '/behandlingstyper', '/prisliste'].includes(location.pathname);
  const isKontaktActive = ['/kontakt', '/kontakt/firmaordning'].includes(location.pathname);

  return (
    <header className="sticky top-0 z-40 bg-[#041032] border-b border-[#0d225d] shadow-md">
      {/* Main Navigation Bar - Solid #041032 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top row: Logo on left, Contact & CTA on right */}
        <div className="flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img
              src={imageUrl(logoUrl, 'width=360,quality=95,format=auto')}
              alt="Djurs Massage"
              className="h-14 sm:h-16 lg:h-18 w-auto object-contain"
            />
          </Link>

          {/* Right Action buttons */}
          <div className="flex items-center gap-3 sm:gap-4">
            <a
              href="tel:28583907"
              className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-white hover:text-slate-200 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-xs text-[#C9A770]" aria-hidden="true" />
              <span>28 58 39 07</span>
            </a>

            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-3.5 py-2 sm:px-5 sm:py-2.5 bg-[#C9A770] text-[#041032] text-xs sm:text-sm font-bold rounded-full hover:bg-[#d8b884] hover:shadow-md transition-all whitespace-nowrap"
            >
              <i className="fa-solid fa-calendar-check text-[#041032]" aria-hidden="true" />
              <span>Book tid</span>
            </a>

            {/* Mobile Hamburger Button (Strictly visible under 1200px) - Ingen boks */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex min-[1200px]:hidden items-center justify-center w-10 h-10 text-white bg-transparent border-0 transition-colors hover:text-[#C9A770]"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn navigationsmenu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-2xl' : 'fa-bars text-xl'}`} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Bottom row: Desktop Navigation under logo, left-aligned (Visible strictly at >= 1200px) */}
        <div className="hidden min-[1200px]:block border-t border-[#0d225d]">
          <nav className="flex items-center justify-start gap-1 xl:gap-2 -mb-px" aria-label="Hovednavigation">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3 py-3 text-sm transition-colors border-b-2 ${
                  isActive
                    ? 'border-white text-white font-bold'
                    : 'border-transparent text-slate-300 hover:text-white'
                }`
              }
            >
              Startside
            </NavLink>

            {/* Behandlinger Dropdown - Clean text, no icons, no descriptions */}
            <div
              className="relative flex items-center"
              onMouseEnter={() => setBehandlingerOpen(true)}
              onMouseLeave={() => setBehandlingerOpen(false)}
            >
              <NavLink
                to="/behandlinger"
                className={() =>
                  `px-3 py-3 text-sm flex items-center gap-1.5 transition-colors border-b-2 ${
                    isBehandlingerActive
                      ? 'border-white text-white font-bold'
                      : 'border-transparent text-slate-300 hover:text-white'
                  }`
                }
              >
                <span>Behandlinger</span>
                <i
                  className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                    behandlingerOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </NavLink>

              {behandlingerOpen && (
                <div className="absolute top-full left-0 w-60 pt-1 z-50">
                  <div className="bg-[#041032] border border-[#0d225d] shadow-xl py-2">
                    <NavLink
                      to="/prisliste"
                      className="block px-4 py-2 text-sm text-white hover:text-[#C9A770] hover:bg-[#07194c] font-medium"
                    >
                      Prisliste & klippekort
                    </NavLink>
                    <div className="my-1 border-t border-[#0d225d]" />
                    <NavLink
                      to="/behandlinger/fysiurgisk-massage"
                      className="block px-4 py-1.5 text-sm text-white hover:text-[#C9A770] hover:bg-[#07194c]"
                    >
                      Fysiurgisk massage
                    </NavLink>
                    <NavLink
                      to="/behandlinger/zoneterapi"
                      className="block px-4 py-1.5 text-sm text-white hover:text-[#C9A770] hover:bg-[#07194c]"
                    >
                      Zoneterapi
                    </NavLink>
                    <NavLink
                      to="/behandlinger/ems-traening"
                      className="block px-4 py-1.5 text-sm text-[#C9A770] hover:text-white hover:bg-[#07194c] font-semibold"
                    >
                      EMS dragt
                    </NavLink>
                    <NavLink
                      to="/behandlinger/relax-massage"
                      className="block px-4 py-1.5 text-sm text-white hover:text-[#C9A770] hover:bg-[#07194c]"
                    >
                      Relax massage
                    </NavLink>
                    <NavLink
                      to="/behandlinger/voksbehandling"
                      className="block px-4 py-1.5 text-sm text-white hover:text-[#C9A770] hover:bg-[#07194c]"
                    >
                      Voksbehandling
                    </NavLink>
                    <NavLink
                      to="/behandlinger/pedicure-fodbehandling"
                      className="block px-4 py-1.5 text-sm text-white hover:text-[#C9A770] hover:bg-[#07194c]"
                    >
                      Pedicure / fodbehandling
                    </NavLink>
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-3 py-3 text-sm transition-colors border-b-2 ${
                  isActive
                    ? 'border-white text-white font-bold'
                    : 'border-transparent text-white/90 hover:text-white'
                }`
              }
            >
              Om os
            </NavLink>

            {/* Kontakt Dropdown - Clean text, no icons, no descriptions */}
            <div
              className="relative flex items-center"
              onMouseEnter={() => setKontaktOpen(true)}
              onMouseLeave={() => setKontaktOpen(false)}
            >
              <NavLink
                to="/kontakt"
                className={() =>
                  `px-3 py-3 text-sm flex items-center gap-1.5 transition-colors border-b-2 ${
                    isKontaktActive
                      ? 'border-white text-white font-bold'
                      : 'border-transparent text-white/90 hover:text-white'
                  }`
                }
              >
                <span>Kontakt</span>
                <i
                  className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                    kontaktOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </NavLink>

              {kontaktOpen && (
                <div className="absolute top-full left-0 w-48 pt-1 z-50">
                  <div className="bg-[#041032] border border-[#0d225d] shadow-xl py-2">
                    <NavLink
                      to="/kontakt"
                      end
                      className="block px-4 py-2 text-sm text-white hover:text-[#C9A770] hover:bg-[#07194c] font-medium"
                    >
                      Kontakt os
                    </NavLink>
                    <NavLink
                      to="/kontakt/firmaordning"
                      className="block px-4 py-2 text-sm text-white hover:text-[#C9A770] hover:bg-[#07194c] font-medium"
                    >
                      Firmaordning
                    </NavLink>
                    <NavLink
                      to="/job"
                      className="block px-4 py-2 text-sm text-white hover:text-[#C9A770] hover:bg-[#07194c] font-medium"
                    >
                      Jobansøgning
                    </NavLink>
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/anmeldelser"
              className={({ isActive }) =>
                `px-3 py-3 text-sm transition-colors border-b-2 ${
                  isActive
                    ? 'border-white text-white font-bold'
                    : 'border-transparent text-white/90 hover:text-white'
                }`
              }
            >
              Anmeldelser
            </NavLink>

            <a
              href={giftcardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-3 text-sm text-white/90 hover:text-white transition-colors border-b-2 border-transparent"
            >
              Klippekort/Gavekort
            </a>

            <NavLink
              to="/betingelser-mm"
              className={({ isActive }) =>
                `px-3 py-3 text-sm transition-colors border-b-2 ${
                  isActive
                    ? 'border-white text-white font-bold'
                    : 'border-transparent text-white/90 hover:text-white'
                }`
              }
            >
              Betingelser mm
            </NavLink>
          </nav>
        </div>
      </div>

      {/* Mobile Drawer (Visible strictly under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-[#0d225d] bg-[#041032] px-4 pt-3 pb-6 max-h-[calc(100vh-5rem)] overflow-y-auto shadow-2xl">
          <div className="flex flex-col space-y-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3 py-2 text-base ${
                  isActive ? 'text-white font-extrabold' : 'text-white/95 font-medium'
                }`
              }
            >
              Startside
            </NavLink>

            {/* Mobile Behandlinger Accordion */}
            <div>
              <button
                type="button"
                onClick={() => setMobileBehandlingerOpen(!mobileBehandlingerOpen)}
                className={`w-full flex items-center justify-between px-3 py-2 text-base text-left ${
                  isBehandlingerActive ? 'text-white font-extrabold' : 'text-white/95 font-medium'
                }`}
              >
                <span>Behandlinger</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    mobileBehandlingerOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
              {mobileBehandlingerOpen && (
                <div className="pl-4 py-1 space-y-1 bg-[#07194c]">
                  <NavLink
                    to="/prisliste"
                    className="block px-3 py-2 text-sm text-white hover:text-[#C9A770] font-medium"
                  >
                    Prisliste & klippekort
                  </NavLink>
                  <NavLink
                    to="/behandlinger/fysiurgisk-massage"
                    className="block px-3 py-1.5 text-xs text-white hover:text-[#C9A770]"
                  >
                    Fysiurgisk massage
                  </NavLink>
                  <NavLink
                    to="/behandlinger/zoneterapi"
                    className="block px-3 py-1.5 text-xs text-white hover:text-[#C9A770]"
                  >
                    Zoneterapi
                  </NavLink>
                  <NavLink
                    to="/behandlinger/ems-traening"
                    className="block px-3 py-1.5 text-xs text-[#C9A770] hover:text-white font-semibold"
                  >
                    EMS dragt
                  </NavLink>
                  <NavLink
                    to="/behandlinger/relax-massage"
                    className="block px-3 py-1.5 text-xs text-white hover:text-[#C9A770]"
                  >
                    Relax massage
                  </NavLink>
                  <NavLink
                    to="/behandlinger/voksbehandling"
                    className="block px-3 py-1.5 text-xs text-white hover:text-[#C9A770]"
                  >
                    Voksbehandling
                  </NavLink>
                  <NavLink
                    to="/behandlinger/pedicure-fodbehandling"
                    className="block px-3 py-1.5 text-xs text-white hover:text-[#C9A770]"
                  >
                    Pedicure / fodbehandling
                  </NavLink>
                </div>
              )}
            </div>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-3 py-2 text-base ${
                  isActive ? 'text-white font-extrabold' : 'text-white/95 font-medium'
                }`
              }
            >
              Om os
            </NavLink>

            {/* Mobile Kontakt Accordion */}
            <div>
              <button
                type="button"
                onClick={() => setMobileKontaktOpen(!mobileKontaktOpen)}
                className={`w-full flex items-center justify-between px-3 py-2 text-base text-left ${
                  isKontaktActive ? 'text-white font-extrabold' : 'text-white/95 font-medium'
                }`}
              >
                <span>Kontakt</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    mobileKontaktOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
              {mobileKontaktOpen && (
                <div className="pl-4 py-1 space-y-1 bg-[#07194c]">
                  <NavLink
                    to="/kontakt"
                    end
                    className="block px-3 py-2 text-sm text-white hover:text-[#C9A770] font-medium"
                  >
                    Kontakt os
                  </NavLink>
                  <NavLink
                    to="/kontakt/firmaordning"
                    className="block px-3 py-2 text-sm text-white hover:text-[#C9A770] font-medium"
                  >
                    Firmaordning
                  </NavLink>
                  <NavLink
                    to="/job"
                    className="block px-3 py-2 text-sm text-white hover:text-[#C9A770] font-medium"
                  >
                    Jobansøgning
                  </NavLink>
                </div>
              )}
            </div>

            <NavLink
              to="/anmeldelser"
              className={({ isActive }) =>
                `px-3 py-2 text-base ${
                  isActive ? 'text-white font-extrabold' : 'text-white/95 font-medium'
                }`
              }
            >
              Anmeldelser
            </NavLink>

            <a
              href={giftcardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-base font-medium text-white/95 hover:text-white"
            >
              Klippekort/Gavekort
            </a>

            <NavLink
              to="/betingelser-mm"
              className={({ isActive }) =>
                `px-3 py-2 text-base ${
                  isActive ? 'text-white font-extrabold' : 'text-white/95 font-medium'
                }`
              }
            >
              Betingelser mm
            </NavLink>
          </div>

          <div className="mt-5 pt-4 border-t border-[#0d225d] space-y-2.5">
            {/* CTA for mobile: remove "Ring" or "Ring på", just the number */}
            <a
              href="tel:28583907"
              className="flex items-center justify-center gap-2 w-full py-2.5 border border-white hover:border-white text-white hover:text-white font-semibold text-xs rounded-full whitespace-nowrap hover:shadow-xs transition-colors"
            >
              <i className="fa-solid fa-phone text-white" aria-hidden="true" />
              <span className="whitespace-nowrap font-bold">28 58 39 07</span>
            </a>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#C9A770] text-[#041032] font-bold text-xs rounded-full hover:shadow-md transition-shadow"
            >
              <i className="fa-solid fa-calendar-check text-[#041032]" aria-hidden="true" />
              <span>Book tid online</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
